/// <reference types="chrome"/>
import { Injectable, OnDestroy } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable, BehaviorSubject, Subject, Subscription, forkJoin, of, from } from 'rxjs'
import { map, first, concatMap, takeWhile, delay as timedelay, takeUntil } from 'rxjs/operators'
import { Platform } from '@ionic/angular';
import { Network } from '@capacitor/network';

import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { UserAppService } from '@/services/user-apps.service';

import { EXTENSION_ID } from '../../common/constants/utils.constants';


@Injectable({
  providedIn: 'root'
})

export class ScriptProcessorService {

  login: any = []
  running: boolean = false
  __show_logs__ = true

  private subject = new Subject<any>()
  private showLoader = new Subject<{showLoader: string}>()
  public iab: any

  no_op = () => {/* no operation - placeholder */};
  log = (...msg) => { if(this.__show_logs__) console.log("👉 ", ...msg) }

  actions: any

  constructor(
    private httpClient: HttpClient,
  ) {

//===================================================================
//          ____  _                 _       _
//         |  _ \(_)___ _ __   __ _| |_ ___| |__   ___ _ __
//         | | | | / __| '_ \ / _` | __/ __| '_ \ / _ \ '__|
//         | |_| | \__ \ |_) | (_| | || (__| | | |  __/ |
//         |____/|_|___/ .__/ \__,_|\__\___|_| |_|\___|_|
//                     |_|
//
// This is a dictionary of all action items in the api, listed here for
// clarity. This design pattern normally refered to as a dispatch table.

    this.actions = {

      "sleep":            this.no_op,  // Implemented
      "wait-for":         this.no_op,  // Implemented
      "wait-for-txt":     this.no_op,
      "wait-for-url":     this.no_op,
      "inject-script-tag":this.no_op,

      // General Input Commands
      "set-input":        this.no_op, // Implemented
      "set-select":       this.no_op,
      "set-radio":        this.no_op,
      "set-checkbox":     this.no_op,
      "set-hidden":       this.no_op,
      "set-textarea":     this.no_op,
      "set-inner-html":   this.no_op,

      // Click - used for buttons, checkboxes, and radio buttons
      "click":            this.no_op, // Implemented

      // Go to Url
      "go-to-url":        this.no_op // Implemented

    };



//===================================================================
//                    _        _   _
//                   / \   ___| |_(_) ___  _ __  ___
//                  / _ \ / __| __| |/ _ \| '_ \/ __|
//                 / ___ \ (__| |_| | (_) | | | \__ \
//                /_/   \_\___|\__|_|\___/|_| |_|___/
//
//


//-------------------------------------------------------------------
// ACTION: sleep
//
// Sleep for a specified amount of time. Consider using wait-for instead.
//
// ---
// ARGUMENTS: (delay = 250)
//

    // Note: Not working if injected to vendors app
    this.actions['sleep'] = async (delay) => {
      let sleep = ms => new Promise(r=>setTimeout(r, ms));
      await sleep(delay);
      return true;
    }


    this.actions['go-to-url'] = (url) => {
      return `

      window.location.href = "${url}";
      return true;
      `
    }


//-------------------------------------------------------------------
// ACTION: set-input
//
// This sets the value for basic html <input ...> elements. It will wait for
// the element to exist by default.
//
// ---
// ARGUMENTS: (selector, value, events = true)
//
// selector: This is a css selector. You can use class, id or other common
//           selector properties. For example:
//               - Get By Id:                 `#username`
//               - Get by Class:              `.username`
//               - Get Input Element by Name: `input[name="username"]`
//               - Get Input Element by Tag:  `input`
//               - Get the 3rd Input Element: `input:nth-of-type(3)`
//
// value:    The value to set the input, plain string is required
//
// events:   Defaults to true to fire the standard input, change and blur events
//
// retries:  No of time to try and set this value, used where page loads are slow. Defailt 10
//
// delay:    Time betweem retries, defaults to 1/4 of a second
//

    this.actions['set-input'] = (selector, value, events = true) => {
      return `

      var nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
      nativeInputValueSetter.call(document.querySelector('${selector}'), '${value}');

      if(${events}) {
        document.querySelector('${selector}').dispatchEvent(new Event('input', {bubbles: true}));
        document.querySelector('${selector}').dispatchEvent(new Event('change', {bubbles: true}));
        document.querySelector('${selector}').dispatchEvent(new Event('blur', {bubbles: true}));
      }

      return true;

      `;
    }



//-------------------------------------------------------------------
// ACTION: click
//
// This action is used for the sole purpose of clicking a button.
//
// ---
// ARGUMENTS: (selector, value, events = true)
//
// selector: This is a css selector. You can use class, id or other common
//           selector properties. For example:
//               - Get By Id:                 `#submit`
//               - Get by Class:              `.submit`
//               - Get by Name:               `button[name="submit"]`
//               - Get by Tag:                `button`
//               - Get the 3rd Button Element:`button:nth-of-type(3)`
//

    this.actions['click'] = (selector, modifier = undefined) => {
      if (typeof modifier === 'number') {
        return `
        document.querySelectorAll(${JSON.stringify(selector)})[${modifier-1}].click()
        return true
        `
      }

      if (typeof modifier === 'string' && modifier?.length) {
        return `
        const buttons = document.querySelectorAll(${JSON.stringify(selector)})
        for (const button of buttons) {
            if (button.textContent.toLowerCase() === ${JSON.stringify(modifier?.toLowerCase())}) {
                button.click()
                return true
            }
        }
        `
      }

      return `
        document.querySelector(${JSON.stringify(selector)}).click()
        return true;
    `
    };



//-------------------------------------------------------------------
// ACTION: wait-for
//
// Wait for an element to appear, only checks that it exists
//
// ---
// ARGUMENTS: (selector, retries = 10, delay = 250)
//
// selector: This is a css selector. You can use class, id or other common
//           selector properties. For example:
//               - Get By Id:                 `#username`
//               - Get by Class:              `.username`
//               - Get Input Element by Name: `input[name="username"]`
//               - Get Input Element by Tag:  `input`
//               - Get the 3rd Input Element: `input:nth-of-type(3)`
//
// events:   Defaults to true to fire the standard input, change and blur events
//
// retries:  No of time to try and set this value, used where page loads are slow. Defailt 10
//
// delay:    Time betweem retries, defaults to 1/4 of a second
//

    this.actions['wait-for'] = async (selector, events = true, retries = 10, delay = 1000) => {
      let sleep = ms => new Promise(r=>setTimeout(r, ms));
      let attempt = 1;
      let done = false
      let found = false

      while(!done) {
        this.log('retrying', attempt)

        let code = `!!document.querySelector('${selector}')`
        this.iab.executeScript(
          {'code': code},
          (response) => {

            if(response[0] !== false) {
              done = true
              found = true
            }
          })

        attempt = ++attempt;
        if( attempt > retries ) done = true

        // Sleep before next retry
        await sleep(delay);
      }

      return found
    }

  }



//===================================================================
//          __        __         _     __ _
//          \ \      / /__  _ __| | __/ _| | _____      __
//           \ \ /\ / / _ \| '__| |/ / |_| |/ _ \ \ /\ / /
//            \ V  V / (_) | |  |   <|  _| | (_) \ V  V /
//             \_/\_/ \___/|_|  |_|\_\_| |_|\___/ \_/\_/
//
// Main event and workflow engine. All the running of the code happens here.

  public start(login) {
    this.login = login

    // this.clearSiteLoading()
    this.nextLoginAction()
  }


  async nextLoginAction() {
    let next = this.login.shift(); // Pop the next command out of the actions list
    if(!next) return; // Exit when shift returns null - we have reached the end
    if(typeof next.action === "undefined") {
      this.nextLoginAction()
      return;
    }

    this.log("Processing Action: ", next.action, next.payload);

    // Different implementation as the delay cannot be injected to the vendors app
    if (['sleep', 'wait-for'].includes(next.action)) {
      let callback = await this.actions[next.action].apply(null, next.payload)
      if (callback) this.nextLoginAction()
    }

    else {

      let code = this.actions[next.action].apply(null, next.payload)
      await this.iab.executeScript({"code": this.IIFE_wrapper(code)},
        async (response) => {
          if(response == false) this.log('Could not complete login action, got response: ', response)
          else this.nextLoginAction()
        });
    }

  }










  // Public Methods
  // --------------------------

  sendMessage(message: string) {
    this.subject.next(message)
  }

  getMessage(): Observable<any> {
    return this.subject.asObservable()
  }

  showSiteLoading(showLoader: string) {
    this.showLoader.next({ showLoader });
  }

  clearSiteLoading() {
    this.showLoader.next(void 0);
  }

  getSiteLoading(): Observable<any> {
    return this.showLoader.asObservable();
  }


  private IIFE_wrapper(code){
    return `\
            (async function(){
              try {

                ${code}

                return true;
              }
              catch(err) {
                return false
              }
            })();`;

  }

}
